/**
 * RCMManagementOverlay - Revenue Cycle Management Overlay
 * Admin-only overlay for billing, claims, and revenue dashboard
 */

import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  DollarSign, CreditCard, Receipt, Calculator, TrendingUp,
  FileText, AlertTriangle, CheckCircle, Clock, RefreshCw,
  Plus, ChevronRight, Loader2, Building, Percent, ArrowUp, ArrowDown
} from "lucide-react";
import api from "@/lib/api";

export default function RCMManagementOverlay({ isOpen, onClose, userRole }) {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [claims, setClaims] = useState([]);

  // Check admin access
  const isAdmin = userRole?.toLowerCase() === "admin" || userRole?.toLowerCase() === "administrator";

  useEffect(() => {
    if (isOpen) {
      fetchDashboard();
      fetchClaims();
    }
  }, [isOpen]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get("/ehr/rcm/dashboard");
      setDashboard(response.data);
    } catch (err) {
      // Demo data
      setDashboard({
        summary: {
          total_revenue: 1250000,
          collected: 980000,
          outstanding: 270000,
          collection_rate: 78.4
        },
        monthly_trend: [
          { month: "Sep", revenue: 95000, collected: 78000 },
          { month: "Oct", revenue: 102000, collected: 85000 },
          { month: "Nov", revenue: 98000, collected: 82000 },
          { month: "Dec", revenue: 110000, collected: 92000 }
        ],
        by_payer: [
          { payer: "Medicare", amount: 680000, percentage: 54 },
          { payer: "Medicaid", amount: 320000, percentage: 26 },
          { payer: "Private Insurance", amount: 180000, percentage: 14 },
          { payer: "Self-Pay", amount: 70000, percentage: 6 }
        ],
        aging: {
          current: 120000,
          days_30: 85000,
          days_60: 45000,
          days_90: 20000
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async () => {
    try {
      const response = await api.get("/ehr/rcm/claims");
      setClaims(response.data.claims || []);
    } catch (err) {
      // Demo claims
      setClaims([
        { id: "CLM-001", patient: "John Smith", amount: 2500, status: "submitted", date: "2025-01-10", payer: "Medicare" },
        { id: "CLM-002", patient: "Mary Johnson", amount: 1800, status: "pending", date: "2025-01-09", payer: "Medicaid" },
        { id: "CLM-003", patient: "Robert Williams", amount: 3200, status: "paid", date: "2025-01-08", payer: "Medicare" },
        { id: "CLM-004", patient: "Patricia Brown", amount: 1500, status: "denied", date: "2025-01-07", payer: "Private Insurance" },
        { id: "CLM-005", patient: "James Davis", amount: 2100, status: "submitted", date: "2025-01-06", payer: "Medicare" }
      ]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/50";
      case "submitted": return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "pending": return "bg-amber-500/20 text-amber-400 border-amber-500/50";
      case "denied": return "bg-red-500/20 text-red-400 border-red-500/50";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  // Access check for non-admins
  if (!isAdmin) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[500px] max-w-[95vw] bg-slate-900 border-slate-700 text-white">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-red-400" />
              Access Restricted
            </SheetTitle>
          </SheetHeader>
          <div className="mt-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Administrator Access Required</h3>
            <p className="text-slate-400">
              Revenue Cycle Management is restricted to administrators only.
            </p>
            <Button onClick={onClose} className="mt-6">Close</Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            Revenue Cycle Management
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Billing, claims, and revenue dashboard
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col mt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3 mb-4 flex-shrink-0">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <DollarSign className="w-4 h-4" />
                    Total Revenue
                  </div>
                  <p className="text-xl font-bold text-white mt-1">
                    ${((dashboard?.summary?.total_revenue || 0) / 1000).toFixed(0)}K
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Collected
                  </div>
                  <p className="text-xl font-bold text-emerald-400 mt-1">
                    ${((dashboard?.summary?.collected || 0) / 1000).toFixed(0)}K
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <Clock className="w-4 h-4 text-amber-400" />
                    Outstanding
                  </div>
                  <p className="text-xl font-bold text-amber-400 mt-1">
                    ${((dashboard?.summary?.outstanding || 0) / 1000).toFixed(0)}K
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <Percent className="w-4 h-4 text-teal-400" />
                    Collection Rate
                  </div>
                  <p className="text-xl font-bold text-teal-400 mt-1">
                    {dashboard?.summary?.collection_rate}%
                  </p>
                </CardContent>
              </Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="bg-slate-800 flex-shrink-0">
                <TabsTrigger value="dashboard" className="data-[state=active]:bg-emerald-600">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Dashboard
                </TabsTrigger>
                <TabsTrigger value="claims" className="data-[state=active]:bg-emerald-600">
                  <FileText className="w-4 h-4 mr-2" />
                  Claims
                </TabsTrigger>
                <TabsTrigger value="aging" className="data-[state=active]:bg-emerald-600">
                  <Clock className="w-4 h-4 mr-2" />
                  A/R Aging
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1 mt-4">
                <TabsContent value="dashboard" className="mt-0 space-y-4">
                  {/* Revenue by Payer */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white">Revenue by Payer</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {dashboard?.by_payer?.map((payer, idx) => (
                          <div key={idx} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-300">{payer.payer}</span>
                              <span className="text-white font-medium">${((payer.amount || 0) / 1000).toFixed(0)}K ({payer.percentage || 0}%)</span>
                            </div>
                            <Progress value={payer.percentage || 0} className="h-2" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Monthly Trend */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white">Monthly Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4">
                        {dashboard?.monthly_trend?.map((month, idx) => (
                          <div key={idx} className="text-center p-3 bg-slate-800 rounded-lg">
                            <p className="text-sm text-slate-400">{month.month}</p>
                            <p className="text-lg font-bold text-white">${((month.collected || 0) / 1000).toFixed(0)}K</p>
                            <p className="text-xs text-slate-500">of ${((month.revenue || 0) / 1000).toFixed(0)}K</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="claims" className="mt-0 space-y-3">
                  {claims.map(claim => (
                    <Card key={claim.id} className="bg-slate-800/50 border-slate-700">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-white">{claim.id}</span>
                              <Badge variant="outline" className={getStatusColor(claim.status)}>
                                {claim.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-400">{claim.patient} • {claim.payer}</p>
                            <p className="text-xs text-slate-500 mt-1">{claim.date}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-white">${claim.amount.toLocaleString()}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="aging" className="mt-0">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white">Accounts Receivable Aging</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-emerald-900/30 border border-emerald-500/30 rounded-lg">
                          <p className="text-sm text-slate-400">Current (0-30 days)</p>
                          <p className="text-2xl font-bold text-emerald-400">${((dashboard?.aging?.current || 0) / 1000).toFixed(0)}K</p>
                        </div>
                        <div className="p-4 bg-blue-900/30 border border-blue-500/30 rounded-lg">
                          <p className="text-sm text-slate-400">31-60 Days</p>
                          <p className="text-2xl font-bold text-blue-400">${((dashboard?.aging?.days_30 || 0) / 1000).toFixed(0)}K</p>
                        </div>
                        <div className="p-4 bg-amber-900/30 border border-amber-500/30 rounded-lg">
                          <p className="text-sm text-slate-400">61-90 Days</p>
                          <p className="text-2xl font-bold text-amber-400">${((dashboard?.aging?.days_60 || 0) / 1000).toFixed(0)}K</p>
                        </div>
                        <div className="p-4 bg-red-900/30 border border-red-500/30 rounded-lg">
                          <p className="text-sm text-slate-400">90+ Days</p>
                          <p className="text-2xl font-bold text-red-400">${((dashboard?.aging?.days_90 || 0) / 1000).toFixed(0)}K</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-4 flex-shrink-0 pt-4 border-t border-slate-700">
              <Button variant="outline" onClick={fetchDashboard} className="border-slate-600">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                New Claim
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { RCMManagementOverlay };
